import { isEqual } from "lodash-es";
import { type FC, type Ref, useRef } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useBoolean } from "usehooks-ts";

import { SectionRole } from "../../shared/types/roles";

import { roleIcon } from "components/materials/home/roles";
import { t } from "i18n/i18n";
import { ConfirmationDialog } from "mds/components/ConfirmationDialog";
import { IconText } from "mds/components/IconText";
import { MenuInstance, MenuItem, MenuRadioGroup, SubMenu } from "mds/components/Menu";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ORDER_BY_CODE } from "mds/hooks/use-ordered";
import { ChevronDownIcon, PersonSingleIcon, TextBooksIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store";
import {
  courseSelectors,
  sectionUserSelectors,
  selectSectionsByTermAndCourse,
  termSelectors,
  userSelectors,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { toastLocalizedOperationError, toastSuccessMessage } from "utils/alerts";
import { getCourseDisplayTitle } from "utils/course";
import { isTemplateSection } from "utils/publishing";
import { SECTION_ROLES, getSectionResourceRole } from "utils/section";
import { getUserFullName } from "utils/user-utils";

export interface UserEnrollmentSubMenuProps {
  userId: string;
  closeMenu?: () => void;
}

type Inputs = {
  courseId: string | null;
  termId: string | null;
  sectionIds: Set<string>;
  newRole: SectionRole;
};

const updateEnrollments = async (
  userId: string,
  sectionUserIdsToEnable: string[],
  sectionUserIdsToDisable: string[],
  sectionIdsToAdd: string[],
  role: SectionRole,
) => {
  const createPromises = sectionIdsToAdd.map((sectionId) =>
    storeApi.section_users.create(
      {
        section_id: sectionId,
        user_id: userId,
        role,
      },
      { skipToast: true },
    ),
  );
  const enablePromises = sectionUserIdsToEnable.map((sectionUserId) =>
    storeApi.section_users.partial_update(
      { id: sectionUserId, is_disabled: false, role },
      { skipToast: true },
    ),
  );
  const disablePromises = sectionUserIdsToDisable.map((sectionUserId) =>
    storeApi.section_users.partial_update(
      { id: sectionUserId, is_disabled: true },
      { skipToast: true },
    ),
  );
  return Promise.all([...createPromises, ...enablePromises, ...disablePromises])
    .then(() =>
      toastSuccessMessage(
        t("user_options_menu.update_enrolled_sections.update_enrollment_success"),
      ),
    )
    .catch(() => toastLocalizedOperationError("update_enrollment_failure"));
};

export const UserEnrollmentSubMenu: FC<UserEnrollmentSubMenuProps> = ({
  userId,
  closeMenu = () => {},
}) => {
  const user = useAppSelector((s) => userSelectors.selectById(s, userId));
  const sectionUsers = useListSelector((s) => sectionUserSelectors.filterByUserId(s, userId));
  const enabledSectionUsers = sectionUsers.filter((cu) => !cu.is_disabled);
  const currentlyEnrolledSectionIds = new Set(enabledSectionUsers.map((cu) => cu.section_id));

  const courses = useAppSelector(courseSelectors.selectAll);
  const orderedCourses = courses.toSorted(ORDER_BY_CODE);
  const courseMenuRef: Ref<MenuInstance | null> = useRef(null);

  const terms = useAppSelector(termSelectors.selectAll);
  const termMenuRef: Ref<MenuInstance | null> = useRef(null);

  const roleMenuRef: Ref<MenuInstance | null> = useRef(null);
  const { value: shouldSelect, toggle: toggleSelectButton } = useBoolean(true);
  const {
    value: confirmationDialogIsOpen,
    setTrue: openConfirmationDialog,
    setFalse: closeConfirmationDialog,
  } = useBoolean(false);

  const { setValue, handleSubmit, watch, control } = useForm<Inputs>({
    defaultValues: {
      courseId: null,
      termId: null,
      sectionIds: currentlyEnrolledSectionIds,
      newRole: SectionRole.STUDENT,
    },
  });

  const courseId = watch("courseId");
  const termId = watch("termId");
  const sectionIds = watch("sectionIds");
  const newRole = watch("newRole");

  const hasChanges = !isEqual(sectionIds, currentlyEnrolledSectionIds);
  const sectionIdsToDisable = new Set(
    [...currentlyEnrolledSectionIds].filter((x) => !sectionIds.has(x)),
  );
  const requestedSectionIds = new Set(
    [...sectionIds].filter((x) => !currentlyEnrolledSectionIds.has(x)),
  );

  const onSubmit: SubmitHandler<Inputs> = () => {
    if (sectionIdsToDisable.size > 0) {
      openConfirmationDialog();
    } else {
      updateEnrollmentsFromForm();
      closeMenu();
    }
  };

  const allSections = useAppSelector((s) => selectSectionsByTermAndCourse(s, termId, courseId));
  // Users cannot be enrolled in template sections, so exclude them from the picker.
  const sections = allSections.filter((section) => !isTemplateSection(section));

  // This is for the confirmation dialog, which shows the names of removed sections
  const disabledSectionNames = Array.from(sectionIdsToDisable)
    .sort()
    .map(
      (disabledSectionId) =>
        sections.find((section) => section.id === disabledSectionId)?.title || "",
    );

  const updateEnrollmentsFromForm = () => {
    const disabledSectionUsers = sectionUsers.filter((cu) => cu.is_disabled);

    // Adding enrollments is a bit complicated; for each course, we need to figure out
    // if the user is already enrolled but disabled, or if they are not enrolled at all.
    const sectionUsersToEnable = disabledSectionUsers.filter((cu) =>
      requestedSectionIds.has(cu.section_id),
    );
    const sectionIdsToAdd = Array.from(requestedSectionIds).filter(
      (sectionId) => !sectionUsersToEnable.some((cu) => cu.section_id === sectionId),
    );

    // Removing enrollments is simpler; we just disable the SectionUser object.
    const sectionUsersToDisable = enabledSectionUsers.filter((cu) =>
      sectionIdsToDisable.has(cu.section_id),
    );
    return updateEnrollments(
      userId,
      sectionUsersToEnable.map((cu) => cu.id),
      sectionUsersToDisable.map((cu) => cu.id),
      sectionIdsToAdd,
      newRole,
    );
  };

  if (!user) {
    return <div className="p-3">{t("common.loading")}</div>;
  }

  return (
    <form className="w-full min-w-[400px] bg-black-tint-99 p-3">
      <h3 className="body-s flex gap-1 pb-5">
        {t("user_options_menu.update_enrolled_sections.title", { name: user.first_name })}
      </h3>

      <div className="flex items-center justify-between gap-2 pb-2">
        <div className="flex items-center gap-2">
          <Controller
            control={control}
            name="courseId"
            render={({ field }) => {
              const options = [
                { id: null, title: t("import_modal.all_courses") },
                ...orderedCourses.map((course) => ({
                  id: course.id,
                  title: getCourseDisplayTitle(course),
                })),
              ] as { id: string | null; title: string }[];
              const selectedTitle =
                options.find((o) => o.id === field.value)?.title || t("import_modal.all_courses");

              return (
                <SubMenu
                  className="dropdown"
                  direction="bottom"
                  instanceRef={courseMenuRef}
                  label={
                    <SquareButton kind="tertiary" size="s">
                      <IconText
                        iconEnd={<ChevronDownIcon />}
                        iconStart={<TextBooksIcon />}
                        text={<span className="max-w-[600px] truncate">{selectedTitle}</span>}
                      />
                    </SquareButton>
                  }
                  openTrigger="clickOnly"
                  position="initial"
                >
                  <MenuRadioGroup {...field}>
                    {options.map((option) => (
                      <MenuItem
                        key={option.id}
                        type="radio"
                        value={option.id}
                        onClick={(e) => {
                          field.onChange(option.id);
                          e.keepOpen = true; // eslint-disable-line no-param-reassign
                          courseMenuRef.current?.closeMenu();
                        }}
                      >
                        <IconText
                          iconStart={<TextBooksIcon />}
                          text={<span className="max-w-[600px] truncate">{option.title}</span>}
                        />
                      </MenuItem>
                    ))}
                  </MenuRadioGroup>
                </SubMenu>
              );
            }}
          />

          <Controller
            control={control}
            name="termId"
            render={({ field }) => {
              const options = [{ id: null, title: t("import_modal.all_terms") }, ...terms] as {
                id: string | null;
                title: string;
              }[];
              const selectedTitle =
                options.find((o) => o.id === field.value)?.title || t("import_modal.all_terms");

              return (
                <SubMenu
                  className="dropdown"
                  direction="bottom"
                  instanceRef={termMenuRef}
                  label={
                    <SquareButton kind="tertiary" size="s">
                      <IconText
                        iconEnd={<ChevronDownIcon />}
                        iconStart={<TextBooksIcon />}
                        text={<span className="max-w-[600px] truncate">{selectedTitle}</span>}
                      />
                    </SquareButton>
                  }
                  openTrigger="clickOnly"
                  position="initial"
                >
                  <MenuRadioGroup {...field}>
                    {options.map((option) => (
                      <MenuItem
                        key={option.id}
                        type="radio"
                        value={option.id}
                        onClick={(e) => {
                          field.onChange(option.id);
                          e.keepOpen = true; // eslint-disable-line no-param-reassign
                          termMenuRef.current?.closeMenu();
                        }}
                      >
                        <IconText
                          iconStart={<TextBooksIcon />}
                          text={<span className="max-w-[600px] truncate">{option.title}</span>}
                        />
                      </MenuItem>
                    ))}
                  </MenuRadioGroup>
                </SubMenu>
              );
            }}
          />
        </div>

        <SquareButton
          kind="tertiary"
          size="s"
          onClick={() => {
            const newSectionIds = new Set(sectionIds);
            sections.forEach((section) => {
              if (shouldSelect) {
                newSectionIds.add(section.id);
              } else {
                newSectionIds.delete(section.id);
              }
            });
            setValue("sectionIds", newSectionIds);
            toggleSelectButton();
          }}
        >
          {shouldSelect ? t("common.select_all") : t("common.unselect_all")}
        </SquareButton>
      </div>

      <div className="mb-2 max-h-[150px] overflow-y-scroll rounded-lg border border-black-tint-90 p-2">
        {/* TODO: add empty state if the selected and term and course code has no sections */}
        {sections.length === 0 ? (
          <div className="text-black-tint-70">{t("copy_to_section_sub_menu.no_sections")}</div>
        ) : (
          <ul>
            {sections.map((section) => (
              <li key={section.id}>
                <label className="flex items-center gap-2 py-1">
                  <input
                    checked={sectionIds.has(section.id)}
                    type="checkbox"
                    onChange={(e) => {
                      const newSectionIds = new Set(sectionIds);
                      if (e.target.checked) {
                        newSectionIds.add(section.id);
                      } else {
                        newSectionIds.delete(section.id);
                      }
                      setValue("sectionIds", newSectionIds);
                    }}
                  />
                  <span className="max-w-[600px] truncate">{section.title}</span>
                  {currentlyEnrolledSectionIds.has(section.id) && (
                    <span className="italic text-black-tint-55">
                      ({t("user_options_menu.update_enrolled_sections.already_enrolled")})
                    </span>
                  )}
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="right-align pb-2 text-black-tint-70">
        {t("user_options_menu.update_enrolled_sections.selection_status", {
          count: requestedSectionIds.size,
          existing_count: currentlyEnrolledSectionIds.size,
        })}
      </div>

      <div className="flex items-center justify-between">
        <div>{t("user_options_menu.update_enrolled_sections.role_in_new_section")}</div>
        <Controller
          control={control}
          name="newRole"
          render={({ field }) => {
            const resourceRole = getSectionResourceRole(field.value);
            const selectedTitle = t(`roles.section_${resourceRole}`) || "Unknown Role";
            const selectedIcon = roleIcon[field.value] || <PersonSingleIcon />;

            return (
              <SubMenu
                className="dropdown"
                direction="top"
                instanceRef={roleMenuRef}
                label={
                  <SquareButton kind="tertiary" size="s">
                    <IconText
                      iconEnd={<ChevronDownIcon />}
                      iconStart={selectedIcon}
                      text={selectedTitle}
                    />
                  </SquareButton>
                }
                openTrigger="clickOnly"
                position="initial"
              >
                <MenuRadioGroup {...field}>
                  {SECTION_ROLES.map((role) => (
                    <MenuItem
                      key={role}
                      type="radio"
                      value={role}
                      onClick={(e) => {
                        field.onChange(role);
                        e.keepOpen = true; // eslint-disable-line no-param-reassign
                        roleMenuRef.current?.closeMenu();
                      }}
                    >
                      <IconText
                        iconStart={roleIcon[role] || <PersonSingleIcon />}
                        text={t(`roles.section_${getSectionResourceRole(role)}`)}
                      />
                    </MenuItem>
                  ))}
                </MenuRadioGroup>
              </SubMenu>
            );
          }}
        />
      </div>

      <div className="flex items-center justify-end gap-2">
        <SquareButton kind="tertiary" size="s" onClick={closeMenu}>
          {t("common.cancel")}
        </SquareButton>

        <SquareButton
          disabled={!hasChanges}
          kind="accent"
          size="s"
          onClick={handleSubmit(onSubmit)}
        >
          {t("user_options_menu.update_enrolled_sections.confirm")}
        </SquareButton>
      </div>

      <ConfirmationDialog
        confirmButtonKind="caution"
        confirmButtonText={t("user_options_menu.update_enrolled_sections.remove_from_section", {
          count: sectionIdsToDisable.size,
        })}
        description={
          <div className="sr-only">
            {t("user_options_menu.update_enrolled_sections.confirmation_dialog_title", {
              count: sectionIdsToDisable.size,
              name: getUserFullName(user),
            })}
          </div>
        }
        open={confirmationDialogIsOpen}
        title={
          <div className="font-bold">
            {t("user_options_menu.update_enrolled_sections.confirm_delete")}
          </div>
        }
        onClose={closeConfirmationDialog}
        onConfirm={() => {
          updateEnrollmentsFromForm();
          closeMenu();
        }}
      >
        <div className="my-6 border-l-2 border-red px-4 font-semibold">
          {t("user_options_menu.update_enrolled_sections.confirmation_dialog_body", {
            count: sectionIdsToDisable.size,
            userName: getUserFullName(user),
            sectionNames: disabledSectionNames.map((name) => `“${name}”`),
          })}
        </div>
      </ConfirmationDialog>
    </form>
  );
};
